'use server'

import { apiRequest, ApiResponse } from '../request'

export type BedType =
    | 'STANDARD'
    | 'ELECTRIC'
    | 'ICU'
    | 'PEDIATRIC'
    | 'NEONATAL'
    | 'BARIATRIC'
    | 'BIRTHING'
    | 'ISOLATION'

export type BedStatus =
    | 'AVAILABLE'
    | 'OCCUPIED'
    | 'RESERVED'
    | 'CLEANING'
    | 'MAINTENANCE'
    | 'BLOCKED'

export interface Bed {
    id: number
    companyId: number
    hospitalId: number
    unitId: number
    unit?: {
        id: number
        name: string
        type: string
    }
    number: string
    type: BedType
    status: BedStatus
    hasOxygen: boolean
    hasVacuum: boolean
    hasAirConditioning: boolean
    hasBathroom: boolean
    isPrivate: boolean
    dailyRate?: number
    active: boolean
    notes?: string
    currentAdmissionId?: number
    currentAdmission?: {
        id: number
        client: {
            id: number
            name: string
        }
    }
    createdAt: string
    updatedAt: string
}

export interface CreateBedData {
    unitId: number
    number: string
    type: BedType
    hasOxygen?: boolean
    hasVacuum?: boolean
    hasAirConditioning?: boolean
    hasBathroom?: boolean
    isPrivate?: boolean
    dailyRate?: number
    notes?: string
}

export interface UpdateBedData extends Partial<CreateBedData> {
    active?: boolean
}

export interface UpdateBedStatusData {
    status: BedStatus
    reason?: string
}

export interface BedFilters {
    hospitalId?: number
    unitId?: number
    status?: BedStatus
    type?: BedType
    available?: boolean
}

export async function getBeds(
    companyId?: number,
    filters?: BedFilters
): Promise<ApiResponse<Bed[]>> {
    return apiRequest<Bed[]>('hospital/beds', {
        params: { companyId, ...filters },
    })
}

export async function getBedsByHospital(
    hospitalId: number,
): Promise<ApiResponse<Bed[]>> {
    return apiRequest<Bed[]>(`hospital/beds/hospital/${hospitalId}`, {})
}

export async function getBed(id: number): Promise<ApiResponse<Bed>> {
    return apiRequest<Bed>(`hospital/beds/${id}`, {})
}

export async function getAvailableBeds(
    unitId?: number
): Promise<ApiResponse<Bed[]>> {
    return apiRequest<Bed[]>('hospital/beds/available', { params: { unitId } })
}

export async function createBed(data: CreateBedData): Promise<ApiResponse<Bed>> {
    return apiRequest<Bed>('hospital/beds', { data }, 'post')
}

export async function updateBed(
    id: number,
    data: UpdateBedData
): Promise<ApiResponse<Bed>> {
    return apiRequest<Bed>(`hospital/beds/${id}`, { data }, 'patch')
}

export async function updateBedStatus(
    id: number,
    data: UpdateBedStatusData
): Promise<ApiResponse<Bed>> {
    return apiRequest<Bed>(`hospital/beds/${id}/status`, { data }, 'patch')
}

export async function deleteBed(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`hospital/beds/${id}`, {}, 'delete')
}

export const BED_TYPE_LABELS: Record<BedType, string> = {
    STANDARD: 'Padrao',
    ELECTRIC: 'Eletrico',
    ICU: 'UTI',
    PEDIATRIC: 'Pediatrico',
    NEONATAL: 'Neonatal',
    BARIATRIC: 'Bariatrico',
    BIRTHING: 'Parto',
    ISOLATION: 'Isolamento',
}

export const BED_STATUS_LABELS: Record<BedStatus, string> = {
    AVAILABLE: 'Disponivel',
    OCCUPIED: 'Ocupado',
    RESERVED: 'Reservado',
    CLEANING: 'Higienizacao',
    MAINTENANCE: 'Manutencao',
    BLOCKED: 'Bloqueado',
}

export const BED_STATUS_COLORS: Record<BedStatus, string> = {
    AVAILABLE: 'badge-success',
    OCCUPIED: 'badge-error',
    RESERVED: 'badge-warning',
    CLEANING: 'badge-info',
    MAINTENANCE: 'badge-ghost',
    BLOCKED: 'badge-neutral',
}
